import { useState } from "react";
import { ArrowUpRight, BadgeCheck } from "lucide-react";
import { workShowcase, WA_DEMO } from "../../lib/site";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const tilts = ["-rotate-2", "rotate-1", "-rotate-1", "rotate-2", "-rotate-2"];
const offsets = ["", "lg:mt-14", "lg:mt-6", "lg:mt-2", "lg:mt-16"];

const PhoneFrame = ({ w, i }) => {
  const [wide, setWide] = useState(false);
  return (
    <div
      className={`relative mx-auto max-w-[290px] rounded-[2.8rem] border border-line bg-black p-2.5 shadow-[0_30px_80px_rgba(0,0,0,0.6)] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${tilts[i % tilts.length]} group-hover:rotate-0 group-hover:-translate-y-2 group-hover:border-brand/50 group-hover:shadow-[0_40px_100px_rgba(0,0,0,0.7),0_0_50px_rgba(204,255,2,0.12)]`}
    >
      <span className="absolute inset-0 rounded-[2.8rem] ring-1 ring-white/10 pointer-events-none" aria-hidden="true" />
      <div className="relative overflow-hidden rounded-[2.2rem] border border-white/5 bg-raise">
        {wide && (
          <img src={w.img} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-50" />
        )}
        <img
          src={w.img}
          alt={w.alt}
          loading="lazy"
          onLoad={(e) => {
            const im = e.currentTarget;
            if (im.naturalWidth / im.naturalHeight > 0.62) setWide(true);
          }}
          className={`relative w-full aspect-[9/19] ${wide ? "object-contain" : "object-cover object-top"} transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]`}
        />
      </div>
      <span className="absolute bottom-5 left-1/2 -translate-x-1/2 h-1 w-16 rounded-full bg-white/25" aria-hidden="true" />
    </div>
  );
};

export const Gallery = () => (
  <section id="work" data-testid="gallery-section" className="relative py-24 sm:py-32 scroll-mt-20 overflow-hidden" aria-label="Our work — real client projects">
    <div className="glow-orb w-[600px] h-[600px] top-40 -right-64 opacity-50" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
        <SectionHeading
          eyebrow="Our Work"
          lines={[<>Real client work,</>, <>framed <em className="italic text-brand">properly.</em></>]}
        />
        <Reveal delay={0.2}>
          <a
            data-testid="gallery-cta-link"
            href={WA_DEMO}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 text-sm text-mute hover:text-brand transition-colors duration-300 font-medium"
          >
            Get yours built
            <span className="btn-arrow-box !w-8 !h-8"><ArrowUpRight size={13} /></span>
          </a>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <p className="font-mono text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-mute mb-12" data-testid="work-trust-strip">
          <span className="text-brand">Shipped for clients in</span> — Varanasi · Bengaluru · Toronto · Dubai · Abu Dhabi · Nairobi · Melbourne
        </p>
      </Reveal>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-16" data-testid="gallery-grid">
        {workShowcase.map((w, i) => (
          <Reveal key={w.name} delay={0.07 * i} className={offsets[i % offsets.length]}>
            <article data-testid={`work-showcase-${i}`} className="group">
              <PhoneFrame w={w} i={i} />
              <div className="mt-7 text-center px-4">
                <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-brand" data-testid={`work-category-${i}`}>
                  {String(i + 1).padStart(2, "0")} · {w.category}
                </p>
                <h3 className="font-display text-2xl text-white font-medium mt-2">{w.name}</h3>
                <p className="text-mute text-sm mt-2 leading-relaxed max-w-xs mx-auto">{w.tagline}</p>
              </div>
            </article>
          </Reveal>
        ))}

        <Reveal delay={0.1} className="lg:mt-14">
          <article data-testid="work-showcase-next" className="group">
            <div className="relative mx-auto max-w-[290px] rounded-[2.8rem] border border-dashed border-brand/40 bg-brand/[0.03] p-2.5 rotate-1 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:rotate-0 group-hover:-translate-y-2 group-hover:border-brand group-hover:bg-brand/[0.07]">
              <div className="rounded-[2.2rem] border border-brand/20 aspect-[9/19] flex flex-col items-center justify-center text-center px-8 gap-5">
                <span className="flex gap-1.5" aria-hidden="true">
                  <span className="w-2.5 h-2.5 bg-brand rotate-45" />
                  <span className="w-2.5 h-2.5 bg-brand/60 rotate-45" />
                  <span className="w-2.5 h-2.5 bg-brand/30 rotate-45" />
                </span>
                <p className="font-display font-light text-3xl text-white leading-tight">
                  Your business, <em className="italic text-brand">next.</em>
                </p>
                <p className="text-mute text-xs leading-relaxed">
                  Free custom demo with your real info — pay only when you love it.
                </p>
                <a
                  data-testid="work-showcase-next-cta"
                  href={WA_DEMO}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-lime inline-flex items-center gap-2 px-5 py-3 text-xs font-bold tracking-wide"
                >
                  Claim Free Demo <ArrowUpRight size={13} />
                </a>
              </div>
            </div>
            <div className="mt-7 text-center px-4">
              <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-brand">The Next Frame</p>
              <h3 className="font-display text-2xl text-white font-medium mt-2">Your Project Here</h3>
              <p className="text-mute text-sm mt-2 leading-relaxed max-w-xs mx-auto">One message on WhatsApp starts it.</p>
            </div>
          </article>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <p className="mt-16 text-center text-mute text-sm inline-flex items-center gap-2 w-full justify-center flex-wrap" data-testid="gallery-trust-line">
          <BadgeCheck size={15} className="text-brand shrink-0" />
          Your business could be the next frame on this wall — free demo first, pay only when you love it.
        </p>
      </Reveal>
    </div>
  </section>
);
